import type { FC, ReactNode } from "react";

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";

import { clsx, Icon } from "@alpacahq/ui/old";

export interface CryptoSelectOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

export interface CryptoSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: CryptoSelectOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  allowWrap?: boolean;
  bottomButton?: ReactNode;
  emptyMessage?: ReactNode;
  errorMessage?: string;
}

export const CryptoSelect: FC<CryptoSelectProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = "Select an option",
  className = "",
  disabled = false,
  allowWrap = false,
  bottomButton,
  emptyMessage,
  errorMessage,
}) => {
  const selectedOption = options.find((option) => option.value === value);

  return (
    <div className={clsx("relative", className)}>
      <label className="mb-1 block font-normal text-gray-800 text-sm">
        {label}
      </label>
      <Listbox value={value} onChange={onChange} disabled={disabled}>
        <ListboxButton className="relative w-full cursor-pointer rounded border border-gray-400 bg-white py-2 pr-10 pl-3 text-left shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 sm:text-sm">
          <div
            className={clsx(
              "flex min-w-0 gap-2",
              allowWrap ? "items-start" : "items-center",
            )}
          >
            {selectedOption?.icon && (
              <div className="flex h-5 w-5 items-center justify-center">
                {selectedOption.icon}
              </div>
            )}
            <div
              className={clsx(
                "min-w-0 flex-1 font-medium",
                allowWrap ? "break-words" : "truncate",
                selectedOption ? "text-gray-950" : "text-gray-500",
              )}
              style={
                allowWrap
                  ? { wordBreak: "break-word", overflowWrap: "anywhere" }
                  : undefined
              }
            >
              {selectedOption ? selectedOption.label : placeholder}
            </div>
          </div>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <Icon name="ChevronDown" className="h-5 w-5 text-gray-800" />
          </span>
        </ListboxButton>
        {(options.length > 0 || emptyMessage || bottomButton) && (
          <ListboxOptions className="absolute z-20 mt-1 flex max-h-60 min-h-[140px] w-full flex-col overflow-hidden rounded border border-gray-400 bg-white text-base focus:outline-none sm:text-sm">
            <div className="min-h-0 flex-1 overflow-auto">
              {options.length > 0 ? (
                <div className="py-1">
                  {options.map((option) => {
                    const isSelected = option.value === value;
                    return (
                      <ListboxOption
                        key={option.value}
                        value={option.value}
                        className={clsx(
                          "relative cursor-pointer select-none px-3.5 py-1.5",
                          isSelected
                            ? "bg-gray-100 text-gray-900"
                            : "text-gray-900 data-[focus]:bg-indigo-100 data-[focus]:text-indigo-900",
                        )}
                      >
                        <div
                          className={clsx(
                            "flex gap-1.5",
                            allowWrap ? "items-start" : "items-center",
                          )}
                        >
                          {option.icon && (
                            <div className="flex h-4 w-4 items-center justify-center">
                              {option.icon}
                            </div>
                          )}
                          <div
                            className={clsx(
                              "flex-1",
                              allowWrap ? "break-words" : "truncate",
                              isSelected ? "font-medium" : "font-normal",
                            )}
                            style={
                              allowWrap
                                ? {
                                    wordBreak: "break-word",
                                    overflowWrap: "anywhere",
                                  }
                                : undefined
                            }
                          >
                            {option.label}
                          </div>
                        </div>
                      </ListboxOption>
                    );
                  })}
                </div>
              ) : (
                emptyMessage
              )}
            </div>
            {bottomButton && <div className="shrink-0 p-2">{bottomButton}</div>}
          </ListboxOptions>
        )}
      </Listbox>
      {errorMessage && (
        <p className="m-0 mt-1 p-0 text-red-500 text-sm">{errorMessage}</p>
      )}
    </div>
  );
};
